/* ───────── Remote kiosk config (sourced from Till attributes via GraphQL) ───────── */

// Internal structure of the (still-pong.*) categoryConfigs JSON blob
export interface CategoryConfigEntry {
  product_layout?: string
  icon?: string
  image_url?: string
  visible?: boolean
}

// All galaga.* attributes stored on the Till, parsed into typed fields.
// (A few legacy pong.* JSON-string blobs remain — see TODOs in parseTillAttributes.)
export interface KioskRemoteConfig {
  // Theme
  colorAccent?: string
  colorAccentHover?: string
  colorBg?: string
  colorSurface?: string
  colorSurface2?: string
  colorText?: string
  colorMuted?: string

  // Branding
  logoUrl?: string | null
  /** Kioskens visningsnamn (titel på startskärmen, manifest-name). */
  appName?: string
  pageTitle?: string
  /** PWA-konfig — påverkar "Lägg till på hemskärmen" på iOS/Android.
   *  Manifest:en byggs dynamiskt vid load baserat på dessa fält så varje
   *  kund kan ha egen ikon + namn på iPaden utan deploy. */
  appIconUrl192?: string | null
  appIconUrl512?: string | null
  /** Themecolor i manifest (status bar / splash background på iOS).
   *  Default = colorAccent. */
  pwaThemeColor?: string

  /** Bildspel/media-lista för startskärmen — används OM den har items.
   *  Annars faller vi tillbaka till videoUrl* (singel video).
   *  Itemtyp auto-detekteras från URL-extension om type=undefined. */
  startScreenMedia?: StartScreenMedia[]

  // Orientation
  layoutMode?: 'portrait' | 'landscape'

  // Layout
  categoryNavStyle?: string
  defaultProductLayout?: string
  categoryColumns?: number // 2 or 3 columns for category grid
  /** Hoppa över kategorigrid:en — kunden landar direkt på "HELA MENYN"-vyn
   *  efter Äta här/Ta med-valet. Användbart för restauranger med få kategorier
   *  (Kebabdudes etc.) där grid:en bara är extra klick. */
  skipCategoryGrid?: boolean
  /** Visa sticky kategoribar i HELA MENYN — klick på en kategori smooth-
   *  scrollar till sektionen, aktiv kategori highlightas medan kund scrollar.
   *  Bara meningsfullt när skipCategoryGrid är på (i vanligt läge har user
   *  redan kategorigrid:en som navigation). */
  fullMenuCategoryBar?: boolean
  /** Justering av kategoribarens innehåll. 'center' centrerar knapparna
   *  när de får plats; vid overflow scrollar de horisontellt från vänster.
   *  'left' (default) vänsterställer alltid. */
  fullMenuCategoryBarAlign?: 'left' | 'center'
  categoryConfigs?: Record<string, CategoryConfigEntry>

  // Collapse
  collapseEnabled?: boolean
  maxCollapsedRows?: number

  // Timeouts
  inactivityTimeout?: number
  countdownDuration?: number
  receiptAutoDismiss?: number

  // Tips
  tipsEnabled?: boolean
  tipPercentages?: number[]

  // Language
  defaultLocale?: string
  availableLocales?: string[]

  // Filler tiles (fills empty grid slots)
  fillerTiles?: FillerTilesConfig
}

export interface FillerTilesConfig {
  enabled: boolean
  brand: { enabled: boolean; tagline: string }
  promos: { enabled: boolean; items: FillerPromo[] }
  category_tips: { enabled: boolean; items: FillerTip[] }
  // Kategorisidans fyllnadsruta — en dedikerad bild/video-banner på
  // kategorisidan som spänner över `span_cols` kolumner. Placeras efter
  // sista kategorin; CSS-grid wrap:ar automatiskt till ny rad om den
  // inte får plats i sista partiella raden.
  category_page?: FillerCategoryPage
}

export interface FillerPromo {
  image_url: string
  headline: string
  subtext: string
}

export interface FillerTip {
  category: string
  image_url: string
  headline: string
  subtext: string
}

/** En slide i startskärmens bildspel. Lagras som JSON-array under
 *  `galaga.branding.startScreenMedia` på Till. */
export interface StartScreenMedia {
  url: string
  type: 'image' | 'video'
  /** Visningstid i sekunder (image-slides). Default 5s. För video används
   *  videons egen längd om durationSec är 0; annars klipps videon. */
  durationSec: number
}

export interface FillerCategoryPage {
  enabled: boolean
  image_url: string // bild eller video (.mp4/.webm/.mov/.m4v)
  headline: string // valfri
  subtext: string // valfri
  span_cols: number // 1-4, hur många grid-kolumner rutan spänner över
}

// Fallback defaults used when the till hasn't configured fillerTiles yet —
// so even a fresh install shows nice fill tiles instead of empty gaps.
export const DEFAULT_FILLER_TILES: FillerTilesConfig = {
  enabled: true,
  brand: { enabled: true, tagline: 'Asiatisk buffé sedan 1997' },
  promos: {
    enabled: true,
    items: [
      { image_url: '', headline: 'HAPPY HOUR', subtext: 'Vardagar 15–17 · −20% på drycker 🍺' },
      { image_url: '', headline: 'STUDENTRABATT', subtext: 'Visa CSN-kort för 10% rabatt 📚' },
      {
        image_url: '',
        headline: 'PONG APP',
        subtext: 'Ladda ner vår app — snabbare beställning 📱',
      },
    ],
  },
  category_tips: {
    enabled: true,
    items: [
      {
        category: 'DESSERT',
        image_url: '',
        headline: 'Glöm inte desserten!',
        subtext: 'Mochi, pannacotta & mer 🍨',
      },
      {
        category: 'DRYCKER',
        image_url: '',
        headline: 'Törstig?',
        subtext: 'Kolla in vårt dryckessortiment 🧋',
      },
      {
        category: 'ÖL & SAKE',
        image_url: '',
        headline: 'Helgstämning?',
        subtext: 'Öl & sake till maten 🍻',
      },
    ],
  },
  category_page: {
    enabled: false,
    image_url: '',
    headline: '',
    subtext: '',
    span_cols: 2,
  },
}

// ── Coercion helpers ──────────────────────────────────────────────────────────
//
// Galaga-scoped attributes arrive as proper JSON values (string|number|boolean|
// array|object) — no more double-encoding. The helpers below just narrow types
// and reject empty/invalid values.
//
// `legacyJson` is kept around for the few `pong.*` blobs that haven't been
// migrated yet (fillerTiles, startScreenMedia, categoryConfigs, …) — those
// still ship as JSON strings.

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}

function bool(v: unknown): boolean | undefined {
  return typeof v === 'boolean' ? v : undefined
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

function arr<T>(v: unknown): T[] | undefined {
  return Array.isArray(v) ? (v as T[]) : undefined
}

function legacyJson<T>(v: unknown): T | undefined {
  if (typeof v !== 'string' || v.length === 0) return undefined
  try {
    return JSON.parse(v) as T
  } catch {
    return undefined
  }
}

// ── Parser ────────────────────────────────────────────────────────────────────

/**
 * Extracts the galaga.* attribute values from the raw GraphQL Till object
 * (plus a handful of un-migrated pong.* JSON blobs) and returns a typed
 * KioskRemoteConfig.
 *
 * Migrated galaga.* fields arrive as proper JSON values (string|number|
 * boolean|array|object). The remaining pong.* blobs still come as JSON
 * strings and are decoded with `legacyJson`.
 */
export function parseTillAttributes(till: Record<string, unknown>): KioskRemoteConfig {
  return {
    // Theme
    colorAccent: str(till.colorAccent),
    colorAccentHover: str(till.colorAccentHover),
    colorBg: str(till.colorBg),
    colorSurface: str(till.colorSurface),
    colorSurface2: str(till.colorSurface2),
    colorText: str(till.colorText),
    colorMuted: str(till.colorMuted),

    // Branding
    logoUrl: str(till.logoUrl) ?? null,
    appName: str(till.appName),
    pageTitle: str(till.pageTitle),
    appIconUrl192: str(till.appIconUrl192) ?? null,
    appIconUrl512: str(till.appIconUrl512) ?? null,
    pwaThemeColor: str(till.pwaThemeColor),
    startScreenMedia: arr<StartScreenMedia>(till.startScreenMedia) ?? [],

    // Orientation
    layoutMode: till.layoutMode === 'landscape' ? 'landscape' : 'portrait',

    // Layout
    categoryNavStyle: str(till.categoryNavStyle),
    defaultProductLayout: str(till.defaultProductLayout),
    categoryColumns: num(till.categoryColumns),
    skipCategoryGrid: bool(till.skipCategoryGrid),
    fullMenuCategoryBar: bool(till.fullMenuCategoryBar),
    fullMenuCategoryBarAlign: till.fullMenuCategoryBarAlign === 'center' ? 'center' : 'left',
    // Still pong.* (legacy JSON-string blob) until migrated to per-Category entities
    categoryConfigs: legacyJson<Record<string, CategoryConfigEntry>>(till.categoryConfigs),

    // Collapse
    collapseEnabled: bool(till.collapseEnabled),
    maxCollapsedRows: num(till.maxCollapsedRows),

    // Timeouts
    inactivityTimeout: num(till.inactivityTimeout),
    countdownDuration: num(till.countdownDuration),
    receiptAutoDismiss: num(till.receiptAutoDismiss),

    // Tips
    tipsEnabled: bool(till.tipsEnabled),
    tipPercentages: arr<number>(till.tipPercentages),

    // Language
    defaultLocale: str(till.defaultLocale),
    availableLocales: arr<string>(till.availableLocales),

    // Filler tiles — still pong.* JSON blob; falls back to default seed.
    // TODO(galaga-migration): remodel as FillerTile entities.
    fillerTiles: legacyJson<FillerTilesConfig>(till.fillerTiles) ?? DEFAULT_FILLER_TILES,
  }
}
